import { Icon } from "@mui/material";
import React, { Component, Fragment } from "react";
import styled from "styled-components";
import { withTranslation } from "react-i18next";

import { LibraryView } from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import { SideBarItem } from "js/editor/PresentationLibrary/PresentationLibrarySideBar";
import PresentationLibraryViewSelect from "js/editor/PresentationLibrary/PresentationLibraryViewSelect";
import UpgradeIcon from "js/Components/UpgradeIcon";
import UpgradeSection from "js/Components/UpgradeSection";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { Gap10 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Divider, UIPaneSideBar, UIPaneWithSideBar } from "js/react/components/UiComponents";
import { SharedSlidesPane } from "js/react/views/AddSlide/Panes/SharedSlidesPane";
import { disposeAllIndexes } from "js/react/views/AddSlide/DataServices";

import PlayerSettingsPane from "./panes/PlayerSettingsPane";
import { PresentationTemplatesPane } from "./panes/PresentationTemplates/PresentationTemplatesPane";
import TeamAssetsPane from "./panes/TeamAssetsPane";
import TeamBrandingGuardrailsPane from "./panes/TeamBrandingGuardrailsPane";
import TeamFoldersPane from "./panes/TeamFoldersPane";
import TeamSharedThemesPane from "./panes/TeamSharedThemesPane";

export const TeamResourceTypes = {
    PRESENTATION_TEMPLATES: "presentation-templates",
    SHARED_SLIDES: "shared-slides",
    TEAM_THEMES: "themes",
    TEAM_FOLDERS: "folders",
    TEAM_BRANDING_GUARDRAILS: "branding-guardrails",
    TEAM_ASSETS: "assets",
    PLAYER_SETTINGS: "player-settings",
    SLIDE_TEMPLATES: "slide-templates"
};

const Styler = styled.div`
    width: 100%;
    height: 100%;
    display: flex;

    .search-container {
        padding-top: 5px;
    }
`;

class TeamResources extends Component {
    state = {
        selectedPane: TeamResourceTypes.PRESENTATION_TEMPLATES
    }

    constructor(props) {
        super(props);

        if (props.pane) {
            this.state.selectedPane = props.pane;
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.pane != prevProps.pane) {
            this.setState({ selectedPane: this.props.pane });
        }
    }

    componentWillUnmount() {
        disposeAllIndexes();
    }

    handleSelectResourceType = type => {
        AppController.showTeamResources({ pane: type });
    }

    // NOTE: We have to use `className="selected"` instead of `props.selected`
    //   because a bug with `styled and` the application of `.dark-mode`
    selectedClass(filter) {
        return filter ? "selected" : "";
    }

    render() {
        const { onReady = () => { }, t } = this.props;
        const { selectedPane } = this.state;
        const { onSwitchType, sideBarWidth, onResizeSidebar } = this.props;

        const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
        const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
        const canManagePlayerSettings = WorkspaceController.actionPermissions[ActionPermissionsObject.PLAYER_SETTINGS].manage;
        const canUpgradeToManagePlayerSettings = (canUpgradeToManageSharedResources && canManagePlayerSettings) || (canManageSharedResources && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PLAYER_SETTINGS, PermissionActionType.MANAGE));
        const canManageBrandGuardrails = WorkspaceController.actionPermissions[ActionPermissionsObject.BRAND_GUARDRAILS].manage;
        const canUpgradeToManageBrandGuardrails = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.BRAND_GUARDRAILS, PermissionActionType.MANAGE);

        return (
            <UIPaneWithSideBar>
                <UIPaneSideBar
                    style={{ width: sideBarWidth }}
                    onResize={onResizeSidebar}
                >
                    <PresentationLibraryViewSelect libraryView={LibraryView.TEAM_RESOURCES} onSwitchType={onSwitchType} />
                    {(canManageSharedResources || canUpgradeToManageSharedResources) && <>
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.PRESENTATION_TEMPLATES)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.PRESENTATION_TEMPLATES)}
                        >
                            <Icon>video_library</Icon>
                            {t("Presentation Templates")}
                        </SideBarItem>
                        <Divider margin={0} />
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.SLIDE_TEMPLATES)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.SLIDE_TEMPLATES)}
                        >
                            <Icon>library_add</Icon>
                            {t("Slide Templates")}
                        </SideBarItem>
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.SHARED_SLIDES)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.SHARED_SLIDES)}
                        >
                            <Icon>library_books</Icon>
                            {t("Shared Slides")}
                        </SideBarItem>
                        <Divider margin={0} />
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.TEAM_THEMES)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.TEAM_THEMES)}
                        >
                            <Icon>color_lens</Icon>
                            {t("Themes")}
                        </SideBarItem>
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.TEAM_ASSETS)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS)}
                        >
                            <Icon>photo_library</Icon>
                            {t("Assets")}
                        </SideBarItem>
                    </>}
                    <Divider margin={0} />
                    {(canManagePlayerSettings || canUpgradeToManagePlayerSettings) &&
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.PLAYER_SETTINGS)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.PLAYER_SETTINGS)}
                        >
                            <Icon>play_circle_outline</Icon>
                            {t("Player Settings")}
                        </SideBarItem>
                    }
                    {(canManageBrandGuardrails || canUpgradeToManageBrandGuardrails) &&
                        <SideBarItem
                            className={this.selectedClass(selectedPane == TeamResourceTypes.TEAM_BRANDING_GUARDRAILS)}
                            onClick={() => this.handleSelectResourceType(TeamResourceTypes.TEAM_BRANDING_GUARDRAILS)}
                        >
                            <Icon>lock</Icon>
                            {t("Brand Guardrails")}
                        </SideBarItem>
                    }
                    {WorkspaceController.isOwner &&
                        <Fragment>
                            <Divider margin={0} />
                            <SideBarItem
                                className={this.selectedClass(selectedPane == TeamResourceTypes.TEAM_FOLDERS)}
                                onClick={() => this.handleSelectResourceType(TeamResourceTypes.TEAM_FOLDERS)}
                            >
                                <Icon>folder</Icon>
                                {t("Team Folders")}
                            </SideBarItem>
                        </Fragment>
                    }
                    {canUpgradeToManageSharedResources &&
                        <UpgradeSection
                            title={t("Unlock Shared Team Resources")}
                            body={t("Upgrade to a Team plan to share templates, themes, and assets—boosting productivity and brand consistency.")}
                            buttonText={t("Upgrade")}
                            buttonIcon={<UpgradeIcon />}
                            buttonOnClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.TEAM,
                                    analytics: { cta: "shared_team_resources" }
                                })}
                        />
                    }
                </UIPaneSideBar>

                <FlexBox fill vertical top>
                    <Styler>
                        <Gap10 />
                        <OnMountedReporter onMounted={onReady} />
                        {selectedPane === TeamResourceTypes.PRESENTATION_TEMPLATES &&
                            <PresentationTemplatesPane
                                editable={true}
                                onReady={onReady}
                                onConfirm={item => AppController.showEditor({ presentationId: item.id })}
                            />
                        }
                        {selectedPane === TeamResourceTypes.SHARED_SLIDES &&
                            <SharedSlidesPane
                                title={t("Shared Slide")}
                                isTemplate={false}
                                resourceType={TeamResourceTypes.SHARED_SLIDES}
                                trackActivity="SharedSlideLibrary"
                                isManageLibrary
                            />
                        }
                        {selectedPane === TeamResourceTypes.SLIDE_TEMPLATES &&
                            <SharedSlidesPane
                                title={t("Slide template")}
                                isTemplate={true}
                                resourceType={TeamResourceTypes.SLIDE_TEMPLATES}
                                trackActivity="slideTemplateLibrary"
                                isManageLibrary
                            />
                        }
                        {selectedPane === TeamResourceTypes.TEAM_THEMES &&
                            <TeamSharedThemesPane />
                        }
                        {selectedPane === TeamResourceTypes.TEAM_FOLDERS &&
                            <TeamFoldersPane />
                        }
                        {selectedPane === TeamResourceTypes.TEAM_BRANDING_GUARDRAILS &&
                            <TeamBrandingGuardrailsPane />
                        }
                        {selectedPane === TeamResourceTypes.TEAM_ASSETS &&
                            <TeamAssetsPane editable />
                        }
                        {selectedPane === TeamResourceTypes.PLAYER_SETTINGS &&
                            <PlayerSettingsPane />
                        }
                    </Styler>
                </FlexBox>
            </UIPaneWithSideBar>
        );
    }
}

export default withTranslation()(TeamResources);
